import { ApiProductType } from '@/core/generated/api';
import type {
    OrderProductResponse,
    PaymentPositionResponse,
    PaymentResponse,
} from '@/core/generated/api';

export interface PaymentPositionView {
    key: string;
    productName: string;
    productUrl: string | null;
    quantity: number | null;
    amount: number | null;
    discount: number | null;
    amountWithDiscount: number | null;
    discounts: Array<{ name: string; amount: number | null }>;
}

export interface PaymentPositionTotals {
    quantity: number | null;
    amount: number | null;
    discount: number | null;
    amountWithDiscount: number | null;
}

/** Преобразует реквизиты платежа в значения для карточки заказа */
export const paymentResponseToSummary = (
    payment: PaymentResponse
): {
    externalId: string;
    amount: string;
    amountWithDiscount: string;
    discount: string;
    status: string;
    paymentUrl: string | null | undefined;
} => ({
    externalId: payment.externalId?.toString() ?? 'Не указано',
    amount: payment.amount == null
        ? 'Не указано'
        : `${payment.amount.toFixed(1)} руб.`,
    amountWithDiscount: payment.amountWithDiscount == null
        ? 'Не указано'
        : `${payment.amountWithDiscount.toFixed(1)} руб.`,
    discount: payment.discount == null
        ? 'Не указано'
        : `${payment.discount.toFixed(1)} руб.`,
    status: payment.status?.name || 'Не указано',
    paymentUrl: (payment.amountWithDiscount ?? 0) > 0
        ? payment.paymentUrl
        : null,
});

/** Объединяет позиции платежа по товарам заказа для компактного отображения */
export const paymentPositionsToView = (
    positions: PaymentPositionResponse[],
    orderProducts: OrderProductResponse[],
): PaymentPositionView[] => {
    const groups = new Map<string, PaymentPositionResponse[]>();

    positions.forEach((position, index) => {
        const key = position.orderProductId
            ? `product:${position.orderProductId}`
            : `position:${index}`;

        const group = groups.get(key);

        if (group) group.push(position);
        else groups.set(key, [position]);
    });

    return Array.from(groups, ([key, group]) => {
        const product = orderProducts.find((item) => item.id === group[0].orderProductId);
        const totals = calculatePaymentPositionTotals(group);
        const name = product?.name || 'Товар не найден';

        const quantitySuffix = totals.quantity != null && totals.quantity > 1
            ? ` (${totals.quantity})`
            : '';

        const discounts = new Map<string, { name: string; amount: number | null }>();

        group.forEach((position) => {
            (position.paymentPositionDiscounts ?? []).forEach((discount) => {
                const discountKey = JSON.stringify([
                    discount.discountType?.id ?? null,
                    discount.discountType?.name ?? null,
                ]);

                const current = discounts.get(discountKey);
                const amount = discount.discount ?? null;

                discounts.set(discountKey, {
                    name: discount.discountType?.name || 'Скидка',
                    amount: current
                        ? addAmounts(current.amount, amount)
                        : amount,
                });
            });
        });

        return {
            key,
            productName: name + quantitySuffix,
            productUrl: product?.productType?.id !== ApiProductType.CustomOperatorService && product?.productId
                ? `/products/${product.productId}`
                : null,
            ...totals,
            discounts: Array.from(discounts.values()),
        };
    });
};

/** Складывает известные суммы платежа с точностью до копейки */
const addAmounts = (left: number | null, right: number | null): number | null => {
    if (left == null || right == null) return null;

    return (Math.round(left * 100) + Math.round(right * 100)) / 100;
};

/** Подсчитывает итоги по позициям платежа без подмены отсутствующих сумм нулём */
export const calculatePaymentPositionTotals = (
    positions: PaymentPositionResponse[],
): PaymentPositionTotals => {
    /** Вычисляет итог выбранного показателя позиций платежа */
    const sum = (field: keyof PaymentPositionTotals): number | null => {
        if (positions.some((position) => position[field] == null)) return null;

        if (field === 'quantity') {
            return positions.reduce((total, position) => total + (position.quantity ?? 0), 0);
        }

        return positions.reduce<number | null>((total, position) =>
            addAmounts(total, position[field] ?? null), 0);
    };

    return {
        quantity: sum('quantity'),
        amount: sum('amount'),
        discount: sum('discount'),
        amountWithDiscount: sum('amountWithDiscount'),
    };
};
